import React, { useEffect, useRef, useState } from "react";
import CloudUpload from "../../assets/icons/cloud_upload.svg";
import PDFFile from "../../assets/icons/pdf_icon.svg";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { fetchAssistantChatGPT } from "../../services/chat.service";
import useWebSocket from "../../hooks/useWebSocket";
import { SHAIA_API } from "../../utils/constans";
import { v4 as uuidv4 } from "uuid";

const FileUploaderCourseProfile = ({ onFileSelect, onClose, selected }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeWbsocket, setActiveWebsocket] = useState(false);
  const [selectedAssetId] = useState(null);
  const [fileError, setFileError] = useState('');
  const { t, i18n } = useTranslation();
  const stopSendingRef = useRef(null);
  const documentIndex = useRef(null);
  const temporalLoadingRef = useRef(false);
  const {
    control,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      files: [],
    },
  });
  const { fields, append } = useFieldArray({
    control,
    name: "files",
  });

  const {
    data: notificationData,
    startPeriodicSend,
    closeConnection,
  } = useWebSocket(SHAIA_API);

  // Simulando una carga de archivo con progreso y actualización del estado.
  const [uploadProgress, setUploadProgress] = useState({});
  // Reemplazar esto con lógica lógica de carga.

  const mutation = useMutation(fetchAssistantChatGPT, {
    onSuccess: (res) => {
      if (res?.messages?.length) {
        const findDescription = res?.messages.find(
          (e) => e?.type === "ai" && e?.data?.content?.includes("<description>")
        );
        const currentContent = findDescription?.data?.content || "";
        const regex = /<description>(.*?)<\/description>/;
        const match = currentContent.match(regex);
        const descriptionContent = match ? match[1] : null;

        if (descriptionContent) {
          updateDescription(descriptionContent);
        }
      }
      temporalLoadingRef.current = false;
      closeConnection();
      setActiveWebsocket(false);
    },
    onError: () => {
      temporalLoadingRef.current = false;
    },
  });

  const updateDescription = (description) => {
    if (documentIndex.current || documentIndex.current === 0) {
      setValue(`files[${documentIndex.current}].description`, description);
      setValue(`files[${documentIndex.current}].asset`, selectedAssetId);
    }

    documentIndex.current = null;
  };

  const simulateFileUpload = (file) => {
    const fileSize = file.size;
    let progress = 0;
    const interval = setInterval(() => {
      progress += fileSize / 10; // Asumiendo que la carga incrementa 10% cada intervalo
      if (progress < fileSize) {
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [file.name]: (progress / fileSize) * 100, // Porcentaje de progreso.
          progress: (progress / fileSize) * 100,
        }));
      } else {
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [file.name]: 100,
          progress: 100,
        }));
        clearInterval(interval);
      }
    }, 500); // Actualiza cada medio segundo
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necesario para permitir el drop
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const newFiles = Array.from(e.dataTransfer.files);
    const allFiles = selectedFiles.concat(newFiles); // Concatena los nuevos archivos con los existentes
    setSelectedFiles(allFiles);
    if (typeof onFileSelect === "function") {
      onFileSelect(allFiles);
    }
    newFiles.forEach((file) => {
      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [file.name]: 0, // Iniciar progreso en 0%
      }));
      simulateFileUpload(file); // Llamar a la función que simula la carga
    });
  };

  const handleFileChange = (event) => {
    event.preventDefault();
    const files = event.target.files;
    const newFiles = Array.from(files);
    const allFiles = selectedFiles.concat(newFiles); 
    const supportedFormats = [
      'application/msword',
      'application/pdf',
      'text/plain',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB
    let isValid = true;

    for (let i = 0; i < files.length; i++) {
      if (!supportedFormats.includes(files[i].type) || files[i].size > maxSize) {
        isValid = false;
        break;
      }
    }

    if (!isValid) {
      setFileError('Solo .doc, .pdf, .pptx y .txt son soportados por cada archivo y deben tener maximo 5MB.');
    } else {
      setFileError('');
      if (typeof onFileSelect === "function") {
        setSelectedFiles(allFiles);
      }
      newFiles.forEach((file) => {
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [file.name]: 0, // Iniciar progreso en 0%
        }));
        append({ file, description: "" });
        simulateFileUpload(file); // Llamar a la función que simula la carga
      });
    }



  };

  const onSubmit = (form) => {
    onFileSelect(form.files);
    setSelectedFiles([]);
    reset();
    onClose();
  };

  const customSubmit = () => {
    handleSubmit(onSubmit)();
  };

  const send = (message) => {
    if (message && message.trim() !== "") {
      const payload = {
        role: "user",
        content: message,
      };
      temporalLoadingRef.current = true;
      mutation.mutate({
        messages: [payload],
        type: "asset_description",
        session_id: uuidv4(),
        parent_session_id: null,
        use_case_id: null,
        lang: i18n.language,
        class_profile_id: selected?.id || null,
        asset_ids: selectedAssetId ? [selectedAssetId] : [],
        response_type: null,
        response_schema: null,
      });
    }
  };

  useEffect(() => {
    // Comienza a enviar mensajes cada 30 segundos

    if (activeWbsocket && selectedAssetId) {
      console.log("Activating periodic send for:", selectedAssetId);
      stopSendingRef.current = startPeriodicSend(
        "assetListener",
        null,
        selectedAssetId,
        5000
      );
    } else if (!activeWbsocket && stopSendingRef.current) {
      console.log("Stopping periodic send");
      stopSendingRef.current(); // Detener el envío si el websocket no está activo
    }

    return () => {
      if (stopSendingRef.current) {
        stopSendingRef.current();
      }
    };
    // eslint-disable-next-line
  }, [startPeriodicSend, activeWbsocket, closeConnection]);

  useEffect(() => {
    if (notificationData) {
      if (notificationData.status) {
        closeConnection();
        const contentUser =
          "Genera la descripcion del documento que te he anexado";
        send(contentUser);
      }
    }
    // eslint-disable-next-line
  }, [notificationData, closeConnection]);

  useEffect(() => {
    return () => {
      closeConnection();
      if (stopSendingRef.current) {
        stopSendingRef.current();
      }
    };
    // eslint-disable-next-line
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-h-full">
      <div className="flex flex-col gap-4 p-4 items-center justify-center max-h-full">
        <div
          className="p-4 border-2 border-dashed border-LightBlue-500/50 rounded-md cursor-pointer flex flex-col items-center justify-center gap-1"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            className="hidden"
            id="file-upload"
            accept=".doc,.pdf,.txt,.ppt,.pptx"
            multiple
            onChange={handleFileChange}
          />
          <label
            htmlFor="file-upload"
            className="flex flex-col items-center justify-center cursor-pointer"
          >
            <span className="w-8 h-6">
              <img src={CloudUpload} alt="empty" />
            </span>
            <span className="input-label text-center">
              {t("carga_archivo_elegir_archivo")}
            </span>
            <span className="input-instruction text-center">
              {t("carga_archivo_formato")}
            </span>
          </label>
          <label
            htmlFor="file-upload"
            className="button-tertiary-rounded !h-auto p-4"
          >
            {t("carga_archivo_buscar")}
          </label>
          {
            fileError && (
              <p className=" text-Red-500 font-bold text-sm">
              {fileError}
            </p>
            )
          }
        </div>
        <ul className="w-full flex flex-col gap-4 max-h-1/2 overflow-y-auto">
          {fields.map((item, index) => (
            <li
              key={index}
              className="flex sm-max:flex-col flex-row justify-between items-start p-4 gap-4 bg-DarkBlue-800 rounded-2 w-full h-auto"
            >
              <div className="flex flex-col bg-LightBlue-500 bg-opacity-[15%] p-4 w-[40%] h-auto rounded-2">
                <div className="flex flex-col w-full">
                  <div>
                    <img src={PDFFile} alt="Archivo" />
                  </div>
                  <div className="text-white whitespace-normal ">
                    {item.file.name}
                  </div>
                </div>
                {uploadProgress[item.file.name] < 100 && (
                  <div className="w-full mt-2">
                    <div className="w-full bg-Grey-200 rounded-full overflow-hidden dark:bg-Grey-700">
                      <div
                        className="bg-LightBlue-600 h-1.5 rounded-full"
                        style={{
                          width: `${uploadProgress[item.file.name] || 0}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 w-full h-auto">
                <div className="text-3 text-shaiaBlue font-bold">
                  {String("Descripción").toUpperCase()}
                </div>
                <div className="text-white text-4 leading-4.5">
                  Explica a Shaia qué es este documento, cómo se usará en el curso y cómo lo puede usar para planear. Máximo 300
                  caracteres.
                </div>
                <div className="w-full flex flex-col gap-2 mt-4 mb-0">
                  <textarea
                    {...register(`files.${index}.description`, {
                      required: "Este campo es obligatorio",
                    })}
                    className="w-full h-32 p-2 bg-[#fefefe21] text-white rounded text-sm"
                    placeholder="Ej. Este documento contiene el plan de estudios detallado..."
                  ></textarea>
                  {errors.files &&
                    errors.files[index] &&
                    errors.files[index].description && (
                      <p className="mb-0 text-Red-500 font-bold text-sm">
                        {errors.files[index].description.message}
                      </p>
                    )}
                </div>
              </div>
            </li>
          ))}
        </ul>
        {selectedFiles.length ? (
          <button
            onClick={() => customSubmit()}
            variant="primary"
            type="button"
            disabled={uploadProgress?.progress < 100}
            className="bg-[#3d96c2] text-[#28345c] font-bold rounded-lg p-3"
          >
            {t("boton_carga_archivos_modal")}
          </button>
        ) : null}
      </div>
    </form>
  );
};

export default FileUploaderCourseProfile;
